import "react-reflex/styles.css";
import {ReflexContainer, ReflexElement, ReflexSplitter} from "react-reflex";
import {useCallback, useEffect, useMemo, useState} from "react";
import DriverResultList from "./DriverResultList";
import RoutesResultMap from "./RoutesResultMap";
import {Button, Callout, Spinner, Text} from "@blueprintjs/core";
import {useRoutePlanningContext, AssignmentDetailWithEndDateTime} from "../../providers/RoutePlanningProvider";
import {RoutePlanningBeforeTotals, RoutePlanningRoute} from "../../generated/graphql";
import styled from "@emotion/styled";
import {MAP_LAYERS} from "../map/MapAnchor";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {COLORS, Constants, UserPreferences} from "../../constants/constants";
import {MAP_PORTAL_ALERT, NewWindowPortalCommon} from "../common/NewWindowPortalCommon";
import {rebuildAssignmentDetails} from "./utils/RoutePlanning.utils";
import {useRoutePlanningRows} from "./hooks/useRoutePlanningRows";
import StopColumnChooser, {ChoosableColumn} from "./components/StopColumnChooser";
import RouteCostCalculator from "./components/RouteCostCalculator";
import {RouteCostTotals} from "./RouteCost.service";
import {buildStopsColumnDefs} from "./utils/stopsColumnDefs";
import {extractJsonPref, usePreferenceContext} from "../../providers/PreferenceProvider";
import {useAppContext} from "../../ApplicationContext";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";
import {ColDef} from "@ag-grid-community/core";

const collapseButtonSize = 30;

type PlanningResultProps = {
  onAssignmentDetailsChange?: (details: {
    assignmentDetails: AssignmentDetailWithEndDateTime[];
    isModified: boolean;
    hasRoutes: boolean;
    unassignedJobIds: number[];
  }) => void;
  resetRef?: React.MutableRefObject<(() => void) | null>;
};

const toRouteCostTotals = (bt: RoutePlanningBeforeTotals | null | undefined): RouteCostTotals | null => {
  if (!bt) return null;
  return {
    totalMiles: bt.totalMiles ?? 0,
    totalTransitSeconds: bt.totalTransitSeconds ?? 0,
    totalRoutes: bt.totalRoutes ?? 0,
    totalDeliveryStops: bt.totalDeliveryStops ?? 0,
    totalPickupStops: bt.totalPickupStops ?? 0
  };
};

const PlanningResult = ({onAssignmentDetailsChange, resetRef}: PlanningResultProps) => {
  const {originalResult, selectedJobs, selectedDrivers, dwellTime} = useRoutePlanningContext();
  const {
    etaFromOptimization: etaFromOptimizationEnabled,
    skills: skillsEnabled,
    existingStopsIP: existingStopsIPEnabled
  } = useFeatureFlagContext();
  const {userPreferences} = usePreferenceContext();
  const {skills} = useDispatchDataContext();
  const {appState} = useAppContext();

  const [hiddenRouteIds, setHiddenRouteIds] = useState<string[]>([]);
  const [routes, setRoutes] = useState<RoutePlanningRoute[] | null>(originalResult?.result?.solution?.routes ?? null);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [isPortalOpen, setIsPortalOpen] = useState(false);
  const [isTrafficActive, setIsTrafficActive] = useState(false);
  const [mapType, setMapType] = useState<string>(MAP_LAYERS.ROADMAP);

  const [isRecalculating, setIsRecalculating] = useState(false);

  // Column chooser state — session-only, NOT persisted to user preferences
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string> | null>(null);
  const [columnOrder, setColumnOrder] = useState<ChoosableColumn[] | null>(null);

  // Derive all columns from stop column definitions (excluding suppressColumnsToolPanel like drag/actions)
  // Also compute default hidden column IDs from initialHide/hide flags to avoid a second buildStopsColumnDefs call.
  const {baseColumns, defaultHiddenColumnIds} = useMemo(() => {
    const dummyColorIndex = () => new Map<string, number>();
    const allCols = buildStopsColumnDefs({
      appState,
      isTimezoneOfJobStop: false,
      etaFromOptimizationEnabled,
      skillsEnabled,
      skills,
      buildJobColoringIndex: dummyColorIndex
    });

    const defaultHidden = new Set<string>();
    allCols
      .filter((col) => !col.pinned && !col.suppressColumnsToolPanel)
      .forEach((col) => {
        const colId = (col.colId ?? col.field) as string;
        if (colId && (col.initialHide || col.hide)) defaultHidden.add(colId);
      });

    const cols = allCols
      .filter((col) => !col.suppressColumnsToolPanel)
      .map((col) => ({
        colId: (col.colId ?? col.field) as string,
        headerName: col.headerName ?? "",
        pinned: !!col.pinned
      }))
      .filter((col) => col.colId);
    // Pinned columns (#, Job#, ETA) always first
    const ordered = [...cols.filter((c) => c.pinned), ...cols.filter((c) => !c.pinned)];
    return {baseColumns: ordered, defaultHiddenColumnIds: defaultHidden};
  }, [appState, etaFromOptimizationEnabled, skillsEnabled, skills]);

  // The active column order (user-reordered or base)
  const choosableColumns = columnOrder ?? baseColumns;

  // Initialize hiddenColumnIds and columnOrder from manifest detail preferences + base defaults (once)
  useEffect(() => {
    if (hiddenColumnIds !== null) return;
    const hidden = new Set<string>();
    const columnState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridColumnState)
      ?.value as ColDef[];

    if (columnState && Array.isArray(columnState)) {
      const savedVisibility = new Map<string, boolean>();
      columnState.forEach((saved: any) => {
        if (typeof saved.hide === "boolean") savedVisibility.set(saved.colId, saved.hide);
      });

      baseColumns
        .filter((col) => !col.pinned)
        .forEach((col) => {
          const savedHide = savedVisibility.get(col.colId);
          // DP-3156: a column added after this layout was saved has no entry here. Fall back to
          // its own default instead of leaving it visible — otherwise every new column arrives
          // switched on for existing users.
          if (savedHide === true || (savedHide === undefined && defaultHiddenColumnIds.has(col.colId))) {
            hidden.add(col.colId);
          }
        });

      // Apply saved column order to match the grid's initial ordering
      const pinned = baseColumns.filter((col) => col.pinned);
      const nonPinnedMap = new Map(baseColumns.filter((col) => !col.pinned).map((col) => [col.colId, col]));
      const orderedNonPinned: ChoosableColumn[] = [];
      const matched = new Set<string>();
      columnState.forEach((saved: any) => {
        const col = nonPinnedMap.get(saved.colId);
        if (col) {
          matched.add(saved.colId);
          orderedNonPinned.push(col);
        }
      });
      // DP-3156: keep a newly added column beside its declared neighbour. Appending unmatched
      // columns at the end put Job Alias far from Order Alias for anyone with a saved layout.
      const nonPinnedBase = baseColumns.filter((col) => !col.pinned);
      nonPinnedBase.forEach((col, baseIndex) => {
        if (matched.has(col.colId)) return;
        const previousBase = nonPinnedBase[baseIndex - 1];
        const anchorIndex = previousBase
          ? orderedNonPinned.findIndex((placed) => placed.colId === previousBase.colId)
          : -1;
        if (anchorIndex > -1) orderedNonPinned.splice(anchorIndex + 1, 0, col);
        else orderedNonPinned.push(col);
      });
      setColumnOrder([...pinned, ...orderedNonPinned]);
    } else {
      defaultHiddenColumnIds.forEach((colId) => hidden.add(colId));
    }
    setHiddenColumnIds(hidden);
  }, [userPreferences, baseColumns, defaultHiddenColumnIds, hiddenColumnIds]);

  const handleToggleColumn = useCallback((colId: string) => {
    setHiddenColumnIds((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  }, []);

  const handleReorderColumns = useCallback((reordered: ChoosableColumn[]) => {
    setColumnOrder(reordered);
  }, []);

  // Sync column order when user drags columns directly in the detail grid
  const handleGridColumnOrderChange = useCallback(
    (columns: {colId: string; pinned: boolean}[]) => {
      const headerMap = new Map(baseColumns.map((col) => [col.colId, col.headerName]));
      setColumnOrder(
        columns.map((col) => ({
          colId: col.colId,
          headerName: headerMap.get(col.colId) ?? "",
          pinned: col.pinned
        }))
      );
    },
    [baseColumns]
  );

  const handlePopOut = useCallback(() => setIsPortalOpen(true), []);
  const handlePortalClose = useCallback(() => setIsPortalOpen(false), []);
  const handleTrafficToggle = useCallback(() => setIsTrafficActive((prev) => !prev), []);
  const handleMapTypeChange = useCallback((type: string) => setMapType(type), []);

  const handleToggleRoute = useCallback((routeId: string) => {
    setHiddenRouteIds((prev) => (prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId]));
  }, []);

  const driverColorMap = useMemo(() => {
    const map = new Map<string, string>();
    (routes ?? []).forEach((route, i) => {
      map.set(route.vehicle_id ?? "", Constants.MAP.ROUTE_COLORS[i % Constants.MAP.ROUTE_COLORS.length]);
    });
    return map;
  }, [routes]);

  // Route grid row state: original/working rows, change detection, recalc write-backs.
  const {
    workingRowData,
    setWorkingRowData,
    unassignedJobIds,
    setUnassignedJobIds,
    isModified,
    handleRouteEtasUpdate,
    resetRows
  } = useRoutePlanningRows({routes, driverColorMap});

  const handleToggleAll = useCallback(() => {
    const allRouteIds = (workingRowData ?? []).map((r) => r.routeId);
    const allHidden = allRouteIds.length > 0 && allRouteIds.every((id) => hiddenRouteIds.includes(id));
    setHiddenRouteIds(allHidden ? [] : allRouteIds);
  }, [workingRowData, hiddenRouteIds]);

  // Initialize routes for map
  useEffect(() => {
    if (routes) return;
    setRoutes(originalResult?.result?.solution?.routes ?? []);
  }, [originalResult, routes]);

  const beforeTotals = useMemo(() => toRouteCostTotals(originalResult?.result?.beforeTotals), [originalResult]);

  // Compute modifiedAssignmentDetails and notify parent
  const modifiedAssignmentDetails = useMemo(() => {
    if (!workingRowData || !originalResult) return null;
    return rebuildAssignmentDetails(workingRowData, originalResult.assignmentDetails, dwellTime * 60);
  }, [workingRowData, originalResult, dwellTime]);

  useEffect(() => {
    if (!modifiedAssignmentDetails) return;
    onAssignmentDetailsChange?.({
      assignmentDetails: modifiedAssignmentDetails,
      isModified,
      hasRoutes: (workingRowData?.length ?? 0) > 0,
      unassignedJobIds
    });
  }, [modifiedAssignmentDetails, isModified, workingRowData, unassignedJobIds, onAssignmentDetailsChange]);

  const handleReset = useCallback(() => {
    resetRows();
    setHiddenRouteIds([]);
  }, [resetRows]);

  // Expose reset to parent via ref
  useEffect(() => {
    if (resetRef) {
      resetRef.current = handleReset;
    }
    return () => {
      if (resetRef) resetRef.current = null;
    };
  }, [resetRef, handleReset]);

  const mapHidden = mapCollapsed || isPortalOpen;

  return (
    <ReflexContainer orientation="vertical">
      <ReflexElement
        flex={mapHidden ? 1 : 0.4}
        style={{
          padding: "16px",
          gap: "16px",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 101,
          overflow: "visible"
        }}
      >
        <HeaderRow>
          <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
            <Text style={{fontSize: "18px"}}>Review the route</Text>
            <RouteCostCalculator
              rows={workingRowData}
              isRecalculating={isRecalculating}
              beforeTotals={existingStopsIPEnabled ? beforeTotals : undefined}
            />
            {isRecalculating && (
              <span
                style={{display: "inline-flex", alignItems: "center", gap: "6px"}}
                data-testid="recalculating-indicator"
              >
                <Spinner size={16} />
                <Text style={{fontSize: "12px", lineHeight: 1, color: COLORS.TEXT_GRAY}}>Processing...</Text>
              </span>
            )}
          </div>
          <HeaderActions>
            {isModified && (
              <ResetButton
                data-testid="reset-planning-result-btn"
                icon="reset"
                size="small"
                onClick={handleReset}
                title="Reset all changes"
              >
                Reset
              </ResetButton>
            )}
            <StopColumnChooser
              columns={choosableColumns}
              hiddenColumnIds={hiddenColumnIds ?? new Set()}
              onToggle={handleToggleColumn}
              onReorder={handleReorderColumns}
            />
          </HeaderActions>
        </HeaderRow>
        {unassignedJobIds.length > 0 && (
          <Callout intent="warning" icon="warning-sign">
            {unassignedJobIds.length} job(s) unassigned. These jobs will not be included when routes are created.
          </Callout>
        )}
        <DriverResultList
          onToggleRoute={handleToggleRoute}
          hiddenRouteIds={hiddenRouteIds}
          workingRowData={workingRowData}
          setWorkingRowData={setWorkingRowData}
          setUnassignedJobIds={setUnassignedJobIds}
          hiddenColumnIds={hiddenColumnIds ?? new Set()}
          columnOrder={columnOrder}
          onColumnOrderChange={handleGridColumnOrderChange}
        />
        {!isPortalOpen && (
          <CollapseButton
            mapCollapsed={mapCollapsed}
            data-testid="collapse-button"
            onClick={(e) => {
              e.stopPropagation();
              setMapCollapsed((prev) => !prev);
            }}
            title={mapCollapsed ? "Restore Map" : "Collapse Map"}
          >
            <CollapseIcon icon={mapCollapsed ? solid("chevron-circle-left") : solid("chevron-circle-right")} />
          </CollapseButton>
        )}
      </ReflexElement>
      <ReflexSplitter propagate={false} style={{pointerEvents: mapHidden ? "none" : "auto"}}>
        <div className="spaces-resize-handle resize-left" />
      </ReflexSplitter>
      <ReflexElement flex={mapHidden ? 0 : 0.6} minSize={mapHidden ? 0 : undefined}>
        {isPortalOpen ? (
          <NewWindowPortalCommon
            target="route-planning-map"
            title="Itinerary Planning Map"
            onClose={handlePortalClose}
            alertTextWhenOpen={MAP_PORTAL_ALERT.WHEN_OPEN}
            alertTextWhenPaused={MAP_PORTAL_ALERT.WHEN_PAUSE}
          >
            <RoutesResultMap
              routes={routes ?? []}
              selectedJobs={selectedJobs}
              selectedDrivers={selectedDrivers}
              hiddenRouteIds={hiddenRouteIds}
              workingRowData={workingRowData}
              onToggleAll={handleToggleAll}
              isTrafficActive={isTrafficActive}
              onTrafficToggle={handleTrafficToggle}
              mapType={mapType}
              onMapTypeChange={handleMapTypeChange}
              isPortalOpen={isPortalOpen}
              onPopOut={handlePopOut}
            />
          </NewWindowPortalCommon>
        ) : (
          <RoutesResultMap
            routes={routes ?? []}
            selectedJobs={selectedJobs}
            selectedDrivers={selectedDrivers}
            hiddenRouteIds={hiddenRouteIds}
            workingRowData={workingRowData}
            onToggleAll={handleToggleAll}
            onRouteEtasUpdate={handleRouteEtasUpdate}
            onRecalcLoadingChange={setIsRecalculating}
            dwellSeconds={dwellTime * 60}
            isTrafficActive={isTrafficActive}
            onTrafficToggle={handleTrafficToggle}
            mapType={mapType}
            onMapTypeChange={handleMapTypeChange}
            isPortalOpen={isPortalOpen}
            onPopOut={handlePopOut}
          />
        )}
      </ReflexElement>
    </ReflexContainer>
  );
};

export default PlanningResult;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ResetButton = styled(Button)`
  &.bp5-button {
    color: ${COLORS.BLUE};
    background: none;
    box-shadow: none;
    border: none;
    font-weight: 700;
    padding: 0;
    min-height: unset;
  }

  &.bp5-button .bp5-icon {
    color: ${COLORS.BLUE};

    svg {
      width: 14px;
      height: 14px;
    }
  }

  &.bp5-button:hover,
  &.bp5-button:active,
  &.bp5-button:focus {
    background: none !important;
    box-shadow: none !important;
    border: none !important;
    outline: none;
    opacity: 0.8;
  }
`;

const CollapseButton = styled.div<{mapCollapsed: boolean}>`
  position: absolute;
  top: 50%;
  right: ${({mapCollapsed}) => (mapCollapsed ? `${collapseButtonSize / 2 + 8}px` : `-${collapseButtonSize / 2}px`)};
  transform: translateY(-50%);
  cursor: pointer;
  width: ${collapseButtonSize}px;
  height: ${collapseButtonSize}px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  background-color: #14305a;
  overflow: hidden;
  border-radius: 50%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
`;

const CollapseIcon = styled(FontAwesomeIcon)`
  width: ${collapseButtonSize * 0.75}px;
  height: ${collapseButtonSize * 0.75}px;
  border-radius: 50%;
  color: #14305a;
  background-color: darkgray;

  &:active,
  &:hover {
    background-color: #ffffff;
  }
`;
